import { useCallback, useEffect, useMemo, useState } from 'react';
import { bankingAPI, reconciliationAPI } from '../../api';
import { SlideOver } from '../ui/SlideOver';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { FormLabel } from '../ui/FormLabel';
import { Card, CardContent } from '../ui/Card';
import {
  AlertTriangle,
  ArrowRightLeft,
  CheckCircle2,
  HandCoins,
  PiggyBank,
  ReceiptText,
  Undo2,
  User,
} from 'lucide-react';

/**
 * Proprietor-drawings modal (plan §6 "Proprietor drawings (§E.1)", §9).
 *
 * Handles all four drawing-type flavours against an equity_drawings-class
 * bank account. The modal auto-restricts the available drawing types to
 * ones that make sense from the SOURCE line's side:
 *
 *   source side = business bank (asset_bank / current / savings / petty):
 *     - transfer_to_drawings
 *     - personal_spend_from_business
 *     - reimbursement_to_proprietor
 *
 *   source side = drawings account (equity_drawings):
 *     - business_spend_from_personal
 *
 * Multiple drawings accounts are disambiguated via `owner_label` on
 * bank_accounts (e.g. "Self" vs "Spouse"). The dropdown surfaces the
 * label so the user can clearly send the event to the right owner.
 *
 * Anti-double-count (plan §9): drawings NEVER hit P&L expense nominals.
 * - transfer_to_drawings / reimbursement_to_proprietor flow through the
 *   internal_transfer path server-side and post two paired
 *   bank_transactions rows (business-side cash out, drawings-side cash in).
 * - personal_spend_from_business and business_spend_from_personal create
 *   a one-sided match whose allocation target is proprietor_drawing_log;
 *   reconcile posts a single bank_transactions row on the drawings
 *   account (direction depends on the flavour). No accounting_entries row
 *   is ever produced. Enforced both at the API validator and the DB
 *   trigger enforce_drawings_allocation_invariant.
 */

const DRAWING_TYPES = {
  transfer_to_drawings: {
    label: 'Transfer to drawings',
    short: 'Transfer',
    icon: ArrowRightLeft,
    description:
      'A regular business-bank to drawings-account transfer. Posts a paired internal transfer (no P&L impact).',
    fromSide: 'business',
  },
  personal_spend_from_business: {
    label: 'Personal spend from business',
    short: 'Personal spend',
    icon: HandCoins,
    description:
      'The proprietor spent personally using a business bank card. Drawings balance goes up; no expense is booked.',
    fromSide: 'business',
  },
  business_spend_from_personal: {
    label: 'Business spend from personal funds',
    short: 'Business spend from personal',
    icon: PiggyBank,
    description:
      'The proprietor paid for a business cost from personal funds. The business now owes them; drawings balance goes down.',
    fromSide: 'drawings',
  },
  reimbursement_to_proprietor: {
    label: 'Reimbursement to proprietor',
    short: 'Reimbursement',
    icon: Undo2,
    description:
      'The business pays the proprietor back for earlier out-of-pocket business spend. Clears unreimbursed log rows.',
    fromSide: 'business',
  },
};

function classifyAccountSide(account) {
  if (!account) return null;
  const lc = account.ledger_class;
  const at = account.account_type;
  if (
    lc === 'asset_bank' ||
    at === 'current_account' ||
    at === 'savings_account' ||
    at === 'petty_cash' ||
    at === 'payment_gateway'
  ) {
    return 'business';
  }
  if (lc === 'equity_drawings' || at === 'proprietor_drawings') {
    return 'drawings';
  }
  return null;
}

const formatMoney = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
  }).format(num);
};

const shortDate = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '—');

export function DrawingsModal({
  open,
  onOpenChange,
  line,
  bankAccountId,
  onMatched,
}) {
  const lineAmount = Number(line?.amount ?? 0);
  const lineAbs = Math.abs(lineAmount);

  const [sourceAccount, setSourceAccount] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [accountsError, setAccountsError] = useState('');

  const sourceSide = useMemo(() => classifyAccountSide(sourceAccount), [sourceAccount]);

  const availableTypes = useMemo(() => {
    if (sourceSide === 'business') {
      return [
        'transfer_to_drawings',
        'personal_spend_from_business',
        'reimbursement_to_proprietor',
      ];
    }
    if (sourceSide === 'drawings') {
      return ['business_spend_from_personal'];
    }
    return [];
  }, [sourceSide]);

  const drawingsAccounts = useMemo(
    () => (accounts || []).filter((a) => classifyAccountSide(a) === 'drawings'),
    [accounts]
  );

  // For business_spend_from_personal the drawings account IS the source
  // line's account; for everything else it's a separate choice.
  const [drawingType, setDrawingType] = useState('');
  const [drawingsAccountId, setDrawingsAccountId] = useState('');
  const [linkedPurchaseId, setLinkedPurchaseId] = useState('');
  const [clearLogIds, setClearLogIds] = useState([]);
  const [unreimbursed, setUnreimbursed] = useState([]);
  const [loadingUnreimbursed, setLoadingUnreimbursed] = useState(false);
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  const typeMeta = drawingType ? DRAWING_TYPES[drawingType] : null;

  const resetFormState = useCallback(() => {
    setDrawingType('');
    setDrawingsAccountId('');
    setLinkedPurchaseId('');
    setClearLogIds([]);
    setUnreimbursed([]);
    setReference(line?.reference || '');
    setNotes('');
    setError('');
    setDone(null);
    setBusy(false);
  }, [line?.reference]);

  useEffect(() => {
    if (!open) return;
    resetFormState();
  }, [open, line?.id, resetFormState]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingAccounts(true);
        setAccountsError('');
        const list = await bankingAPI.getAccounts({ include_inactive: false });
        if (cancelled) return;
        setAccounts(list || []);
        const src = (list || []).find((a) => a.id === bankAccountId) || null;
        setSourceAccount(src);
      } catch (err) {
        if (cancelled) return;
        setAccountsError(
          err?.response?.data?.error || err.message || 'Could not load accounts'
        );
      } finally {
        if (!cancelled) setLoadingAccounts(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, bankAccountId]);

  // Auto-pick the default drawing type per side: business -> transfer,
  // drawings -> business_spend_from_personal.
  useEffect(() => {
    if (!sourceSide || drawingType) return;
    if (sourceSide === 'business') setDrawingType('transfer_to_drawings');
    else if (sourceSide === 'drawings') setDrawingType('business_spend_from_personal');
  }, [sourceSide, drawingType]);

  // For business_spend_from_personal auto-pin drawings account to source.
  useEffect(() => {
    if (drawingType === 'business_spend_from_personal' && sourceAccount) {
      setDrawingsAccountId(sourceAccount.id);
    }
  }, [drawingType, sourceAccount]);

  // Auto-pick a single drawings account if there's only one to choose from.
  useEffect(() => {
    if (drawingType === 'business_spend_from_personal') return;
    if (!drawingsAccountId && drawingsAccounts.length === 1) {
      setDrawingsAccountId(drawingsAccounts[0].id);
    }
  }, [drawingType, drawingsAccountId, drawingsAccounts]);

  // Fetch unreimbursed spends when we enter reimbursement flow and have a
  // target drawings account.
  useEffect(() => {
    if (drawingType !== 'reimbursement_to_proprietor' || !drawingsAccountId) {
      setUnreimbursed([]);
      setClearLogIds([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingUnreimbursed(true);
        const r = await reconciliationAPI.listUnreimbursedDrawings(drawingsAccountId);
        if (cancelled) return;
        setUnreimbursed(r?.entries || []);
      } catch (_) {
        if (!cancelled) setUnreimbursed([]);
      } finally {
        if (!cancelled) setLoadingUnreimbursed(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [drawingType, drawingsAccountId]);

  const toggleLogId = (id) =>
    setClearLogIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const canSubmit = useMemo(() => {
    if (!line || !bankAccountId || !sourceSide || lineAbs === 0 || busy) return false;
    if (!drawingType || !drawingsAccountId) return false;
    return true;
  }, [line, bankAccountId, sourceSide, lineAbs, busy, drawingType, drawingsAccountId]);

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError('');
    try {
      const payload = {
        drawing_type: drawingType,
        drawings_account_id: drawingsAccountId,
        source_statement_line_id: line.id,
        reference: reference || null,
        notes: notes || null,
      };
      if (drawingType === 'business_spend_from_personal' && linkedPurchaseId) {
        payload.linked_purchase_id = linkedPurchaseId;
      }
      if (drawingType === 'reimbursement_to_proprietor' && clearLogIds.length > 0) {
        payload.clear_log_ids = clearLogIds;
      }
      const result = await reconciliationAPI.createProprietorDrawing(payload);
      setDone(result);
      if (onMatched) onMatched(result);
    } catch (err) {
      setError(
        err?.response?.data?.error || err.message || 'Could not create drawing'
      );
    } finally {
      setBusy(false);
    }
  };

  const sourceSideValid = sourceSide === 'business' || sourceSide === 'drawings';
  const TypeIcon = typeMeta ? typeMeta.icon : ReceiptText;

  return (
    <SlideOver
      open={open}
      onOpenChange={onOpenChange}
      title="Record proprietor drawings event"
    >
      {!line ? (
        <div className="text-xs text-gray-500">No statement line selected.</div>
      ) : (
        <div className="space-y-4">
          {/* Source line summary */}
          <Card>
            <CardContent className="p-3 text-xs">
              <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                <TypeIcon className="h-3.5 w-3.5" />
                {sourceSide === 'drawings' ? 'Drawings account (this side)' : 'Business bank (this side)'}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Date</div>
                  <div>{shortDate(line.transaction_date)}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Description</div>
                  <div className="truncate">{line.description || '—'}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Amount</div>
                  <div
                    className={
                      lineAmount < 0
                        ? 'font-semibold text-red-700 dark:text-red-400'
                        : 'font-semibold text-green-700 dark:text-green-400'
                    }
                  >
                    {formatMoney(lineAbs)}
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Account</div>
                  <div className="truncate">
                    {sourceAccount?.account_name || '—'}
                    {sourceAccount?.owner_label
                      ? ` · ${sourceAccount.owner_label}`
                      : ''}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Source-side validity check */}
          {!loadingAccounts && !sourceSideValid && (
            <Card className="border-red-400 bg-red-50 dark:bg-red-950/30">
              <CardContent className="flex gap-2 p-3 text-xs text-red-700 dark:text-red-300">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span>
                  Drawings events can only originate on a business bank
                  account (asset_bank) or a proprietor-drawings account
                  (equity_drawings). This account is neither.
                </span>
              </CardContent>
            </Card>
          )}

          {sourceSideValid && drawingsAccounts.length === 0 && !loadingAccounts && (
            <Card className="border-amber-400 bg-amber-50 dark:bg-amber-950/30">
              <CardContent className="flex gap-2 p-3 text-xs text-amber-800 dark:text-amber-200">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span>
                  No proprietor-drawings accounts found for this tenant.
                  Create one (e.g. "Drawings — Self" or "Drawings — Spouse")
                  on the banking page first.
                </span>
              </CardContent>
            </Card>
          )}

          {sourceSideValid && !done && (
            <Card className="border-blue-300 bg-blue-50 dark:bg-blue-950/30">
              <CardContent className="p-3 text-[11px] text-blue-800 dark:text-blue-200">
                <div className="flex gap-2">
                  <ArrowRightLeft className="h-3.5 w-3.5 flex-shrink-0" />
                  <span>
                    Drawings events are equity movements — reconciling
                    will move the drawings-account balance but will
                    <strong> never</strong> post a P&amp;L expense entry.
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          {accountsError && (
            <Card className="border-red-400 bg-red-50 dark:bg-red-950/30">
              <CardContent className="flex gap-2 p-3 text-xs text-red-700 dark:text-red-300">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span>{accountsError}</span>
              </CardContent>
            </Card>
          )}

          {done ? (
            <Card className="border-green-500 bg-green-50 dark:bg-green-950/30">
              <CardContent className="p-4 text-xs">
                <div className="flex items-center gap-2 font-semibold text-green-800 dark:text-green-200">
                  <CheckCircle2 className="h-4 w-4" />
                  Drawings event recorded
                  {done?.drawingType ? ` (${DRAWING_TYPES[done.drawingType]?.short || done.drawingType})` : ''}.
                </div>
                <div className="mt-2 text-[11px] text-green-800 dark:text-green-200">
                  Reconcile the draft match(es) to post the bank
                  transactions on the drawings account and complete the
                  equity-ledger movement.
                  {Array.isArray(done?.cleared) && done.cleared.length > 0 && (
                    <>
                      {' '}
                      {done.cleared.length} unreimbursed spend
                      {done.cleared.length === 1 ? '' : 's'} marked as
                      reimbursed.
                    </>
                  )}
                </div>
                <div className="mt-3">
                  <Button size="sm" onClick={() => onOpenChange(false)}>
                    Close
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : sourceSideValid && drawingsAccounts.length > 0 ? (
            <>
              {error && (
                <Card className="border-red-400 bg-red-50 dark:bg-red-950/30">
                  <CardContent className="flex gap-2 p-3 text-xs text-red-700 dark:text-red-300">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    <span>{error}</span>
                  </CardContent>
                </Card>
              )}

              {/* Drawing type picker */}
              <div>
                <FormLabel>Drawing type</FormLabel>
                <div className="grid grid-cols-1 gap-2">
                  {availableTypes.map((t) => {
                    const meta = DRAWING_TYPES[t];
                    const Icon = meta.icon;
                    const active = drawingType === t;
                    return (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setDrawingType(t)}
                        className={`flex items-start gap-2 rounded border p-3 text-left text-xs transition ${
                          active
                            ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-400 dark:bg-blue-950/30'
                            : 'border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800/50'
                        }`}
                      >
                        <Icon className="mt-0.5 h-4 w-4 flex-shrink-0" />
                        <div>
                          <div className="font-semibold">{meta.label}</div>
                          <div className="mt-0.5 text-[11px] text-gray-600 dark:text-gray-400">
                            {meta.description}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Drawings account picker (hidden when it's auto-pinned
                  to the source for business_spend_from_personal). */}
              {drawingType !== 'business_spend_from_personal' && (
                <div>
                  <FormLabel>
                    <span className="inline-flex items-center gap-1">
                      <User className="h-3 w-3" />
                      Drawings account (Self / Spouse / etc.)
                    </span>
                  </FormLabel>
                  <Select
                    value={drawingsAccountId}
                    onChange={(e) => setDrawingsAccountId(e.target.value)}
                    disabled={loadingAccounts}
                  >
                    <option value="">
                      {loadingAccounts ? 'Loading accounts…' : 'Select drawings account…'}
                    </option>
                    {drawingsAccounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.account_name}
                        {a.owner_label ? ` — ${a.owner_label}` : ''}
                      </option>
                    ))}
                  </Select>
                </div>
              )}

              {/* business_spend_from_personal extras */}
              {drawingType === 'business_spend_from_personal' && (
                <div>
                  <FormLabel>Linked purchase id (optional)</FormLabel>
                  <Input
                    value={linkedPurchaseId}
                    onChange={(e) => setLinkedPurchaseId(e.target.value)}
                    placeholder="UUID of existing purchase"
                  />
                  <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                    If this outlay matches an existing purchase record,
                    link it here so the purchase shows it was funded by
                    the proprietor. Leave blank to log a standalone
                    event.
                  </div>
                </div>
              )}

              {/* reimbursement_to_proprietor extras */}
              {drawingType === 'reimbursement_to_proprietor' && (
                <div>
                  <FormLabel>Clear unreimbursed spends (optional)</FormLabel>
                  {loadingUnreimbursed ? (
                    <div className="text-xs text-gray-500">Loading unreimbursed entries…</div>
                  ) : unreimbursed.length === 0 ? (
                    <div className="rounded border border-dashed border-gray-300 p-3 text-xs text-gray-600 dark:border-gray-700 dark:text-gray-400">
                      No unreimbursed business-spend-from-personal rows
                      on this drawings account. The reimbursement will
                      still be recorded; nothing will be flagged as
                      cleared.
                    </div>
                  ) : (
                    <div className="max-h-48 space-y-1 overflow-auto rounded border border-gray-200 p-2 dark:border-gray-700">
                      {unreimbursed.map((u) => {
                        const checked = clearLogIds.includes(u.id);
                        return (
                          <label
                            key={u.id}
                            className={`flex cursor-pointer items-center gap-2 rounded p-1.5 text-xs ${
                              checked
                                ? 'bg-blue-50 dark:bg-blue-950/30'
                                : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleLogId(u.id)}
                            />
                            <span className="flex-1 truncate">
                              {shortDate(u.transaction_date)} · {formatMoney(Number(u.amount))}
                              {u.linked_purchase_num
                                ? ` · Purchase ${u.linked_purchase_num}`
                                : ''}
                              {u.notes ? ` · ${u.notes}` : ''}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <FormLabel>Reference</FormLabel>
                  <Input
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    placeholder="e.g. Drawings Apr 2026"
                  />
                </div>
                <div>
                  <FormLabel>Notes</FormLabel>
                  <Input
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Optional note"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={busy}
                >
                  Cancel
                </Button>
                <Button onClick={submit} disabled={!canSubmit}>
                  {busy ? 'Saving…' : 'Record drawings event'}
                </Button>
              </div>
            </>
          ) : null}
        </div>
      )}
    </SlideOver>
  );
}

export default DrawingsModal;
